import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import CaseMarkPanel from './CaseMarkPanel';
import CaseReviewCard from './CaseReviewCard';

import backend from 'utils/backend';
import featureFlags from 'utils/feature_flags/feature_flags';

// Display precedence: the latest HUMAN record supersedes TEACHER records as the
// case's grade of record; otherwise the latest record wins.
const pickDisplayMarkKey = (marks) => {
  const latestByCreatedAt = (records) => {
    return records.reduce((latest, record) => {
      return !latest || record.createdAt > latest.createdAt ? record : latest;
    }, null);
  };
  const humanRecords = marks.filter((mark) => mark.markerType === 'HUMAN');
  const displayRecord = latestByCreatedAt(humanRecords.length ? humanRecords : marks);

  return displayRecord ? displayRecord.markKey : null;
};

const CaseMarks = (props) => {
  const {
    caseData,
  } = props;
  const caseRn = caseData.caseRn;
  const domainGuideRn = caseData.domainGuideRn || null;
  const [marks, setMarks] = React.useState([]);
  const [marksLoaded, setMarksLoaded] = React.useState(false);
  const [marksLoadFailed, setMarksLoadFailed] = React.useState(false);
  const [caseGuide, setCaseGuide] = React.useState(null);
  const [caseGuideLoaded, setCaseGuideLoaded] = React.useState(false);

  React.useEffect(() => {
    // GET /case/{rn}/marks only exists once engine PR3 is deployed.
    if (!featureFlags.oversightMarkingReadsEnabled) {
      return undefined;
    }

    let isActive = true;

    setMarks([]);
    setMarksLoaded(false);
    setMarksLoadFailed(false);

    const loadMarks = async () => {
      try {
        const marksResponse = await backend.case.getCaseMarks(caseRn);

        if (isActive) {
          setMarks(marksResponse?.marks || []);
          setMarksLoaded(true);
        }
      } catch {
        if (isActive) {
          setMarks([]);
          setMarksLoaded(true);
          setMarksLoadFailed(true);
        }
      }
    };

    loadMarks();

    return () => {
      isActive = false;
    };
  }, [caseRn]);

  React.useEffect(() => {
    // The guide only feeds the review card's category dropdown. It is resolved
    // by the case's domainGuideRn, ACTIVE guides only — the exact resolution
    // the backend's review endpoint uses (an archived guide 404s there), so
    // the card offers the same rubric the review will be validated against.
    if (!featureFlags.oversightCaseReviewEnabled) {
      return undefined;
    }

    let isActive = true;

    setCaseGuide(null);
    setCaseGuideLoaded(false);

    if (!domainGuideRn) {
      setCaseGuideLoaded(true);
      return undefined;
    }

    const loadCaseGuide = async () => {
      try {
        const domainGuideResponse = await backend.domainGuide.getDomainGuide(domainGuideRn);
        const domainGuide = domainGuideResponse?.domainGuide || null;

        if (isActive) {
          setCaseGuide(domainGuide?.status === 'ACTIVE' ? domainGuide : null);
          setCaseGuideLoaded(true);
        }
      } catch {
        if (isActive) {
          setCaseGuide(null);
          setCaseGuideLoaded(true);
        }
      }
    };

    loadCaseGuide();

    return () => {
      isActive = false;
    };
  }, [domainGuideRn]);

  const submitReview = async (review) => {
    const reviewResponse = await backend.case.createReview(caseRn, review);
    const createdMark = reviewResponse?.mark;

    if (createdMark) {
      setMarks((currentMarks) => [
        createdMark,
        ...currentMarks,
      ]);
    }
  };

  const displayMarkKey = React.useMemo(() => {
    return pickDisplayMarkKey(marks);
  }, [marks]);

  return (
    <Stack gap={1.5}>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '20px',
          color: '#050A14',
          textDecoration: 'underline',
          textDecorationColor: '#050A14',
          textUnderlineOffset: '3px',
        }}
      >
        <Translate expression='case.Detail.Marks.Title' />
      </Typography>
      {
        featureFlags.oversightMarkingReadsEnabled &&
        <CaseMarkPanel
          displayMarkKey={displayMarkKey}
          marks={marks}
          loaded={marksLoaded}
          loadFailed={marksLoadFailed}
        />
      }
      {
        featureFlags.oversightCaseReviewEnabled &&
        <CaseReviewCard
          caseGuide={caseGuide}
          caseGuideLoaded={caseGuideLoaded}
          submitReview={submitReview}
        />
      }
    </Stack>
  );
};

CaseMarks.propTypes = {
  caseData: PropTypes.object.isRequired,
};

export default React.memo(CaseMarks);
